import {
  realStateAgentPercentage,
  auctionCommisionPercentage,
  iptuPercentage,
  modalityReverseCriteria,
  monthsToPay,
  yearInMonths,
  banks,
  monthsToSell,
} from "@/constants";

import {
  convertPercentageToDecimal,
  convertPercentageToNormal,
} from "@/helper/currency";

import taxes from "@/static/taxes.json";

import type {
  Bank,
  House,
  LeilaoSQLCustom,
  Modality,
  ViabilityProperty,
} from "@/types";

type OptionalPricingFields = {
  porcentagem_entrada?: number;
  comissao_leiloeiro?: number;
  frequencia_iptu?: ViabilityProperty["frequencia_iptu"];
  valor_iptu?: number;
  juros_anual?: number;
  taxa_escritura?: number;
  taxa_frj?: number;
  taxa_itbi?: number;
  taxa_registro?: number;
  prazo_de_venda_meses?: number;
};

type CalculateGeneralValuesProps = Omit<
  ViabilityProperty,
  keyof OptionalPricingFields
> &
  OptionalPricingFields;

export const calculatePrice = (
  property: Pick<LeilaoSQLCustom, "preco2" | "preco1">
) => property.preco2 || property.preco1;

export const calculateDiscount = ({
  auctionPrice,
  valuationPrice,
}: {
  auctionPrice: number;
  valuationPrice: number;
}) => {
  return valuationPrice && auctionPrice > 0
    ? Number(
        (((valuationPrice - auctionPrice) / valuationPrice) * 100).toFixed(2)
      )
    : 0;
};

export const bankLoanPercentage = (bank?: Bank, modality?: Modality) => {
  if (!hasAuctionCommision(modality)) {
    return 100;
  }

  switch (bank) {
    case "santander":
      return 0.2; // 20% entry financing
    case "bradesco":
      return 0.25; // 25% entry financing
    default:
      return banks.caixa.loanPercentage;
  }
};

export const annualInterestRatePercentage = (modality?: Modality) => {
  const defaultAnnualInterestRate = 12;
  if (!modality) return defaultAnnualInterestRate;

  switch (modality) {
    case "Venda Direta Online":
      return 0;
    case "Venda Online":
      return 0;
    default:
      return defaultAnnualInterestRate;
  }
};

export const formatTwoDecimals = (num: number | null): number => {
  if (!num) return 0;

  return Number(num.toFixed(2));
};

export const getTaxIPTU = (
  valorArrematacao: number,
  frequency: ViabilityProperty["frequencia_iptu"],
  percentage?: number
) => {
  const value =
    (valorArrematacao *
      (convertPercentageToDecimal(percentage) ?? iptuPercentage)) /
    (frequency === "anual" ? 1 : yearInMonths);

  return formatTwoDecimals(value);
};

export const getTaxCondominium = (
  tipo: CalculateGeneralValuesProps["tipo"]
) => {
  return tipo === "apartamento" ? 350 : 0;
};

export const getTaxAuctionCommision = (
  valorArrematacao: number,
  modalidadeDeVenda?: Modality,
  percentage?: number
) => {
  if (!hasAuctionCommision(modalidadeDeVenda)) {
    return 0;
  }

  const value =
    valorArrematacao *
    (convertPercentageToDecimal(percentage) ?? auctionCommisionPercentage);

  return formatTwoDecimals(value);
};

export const getAuctionCommisionPercentage = (
  modalidadeDeVenda?: Modality,
  percentage?: number
) => {
  if (!hasAuctionCommision(modalidadeDeVenda)) {
    return 0;
  }

  const value =
    percentage ?? convertPercentageToNormal(auctionCommisionPercentage);

  return formatTwoDecimals(value);
};

export const getTaxITBI = (
  valorArrematacao: number,
  uf: House["uf"],
  percentage?: number
) => {
  const value =
    valorArrematacao *
    (convertPercentageToDecimal(percentage) ?? getTaxITBIPercentage(uf));

  return formatTwoDecimals(value);
};

export const getTaxFRJ = (
  valorArrematacao: number,
  uf: House["uf"],
  percentage?: number
) => {
  const value =
    valorArrematacao *
    (convertPercentageToDecimal(percentage) ?? getTaxFRJPercentage(uf));

  return formatTwoDecimals(value);
};

export const getTaxEscriture = (
  valorArrematacao: number,
  uf: House["uf"],
  percentage?: number
) => {
  const value =
    valorArrematacao *
    (convertPercentageToDecimal(percentage) ?? getTaxEscriturePercentage(uf));

  return formatTwoDecimals(value);
};

export const getTaxRegister = (
  valorArrematacao: number,
  uf: House["uf"],
  percentage?: number
) => {
  const value =
    valorArrematacao *
    (convertPercentageToDecimal(percentage) ?? getTaxRegisterPercentage(uf));

  return formatTwoDecimals(value);
};

export const getTaxRealStateAgent = (
  valorAvaliacao: number,
  percentage?: number
) => {
  const value =
    valorAvaliacao *
    (convertPercentageToDecimal(percentage) ?? realStateAgentPercentage);

  return formatTwoDecimals(value);
};

export const getTaxConsultantService = (
  valorAvaliacao: number,
  percentage?: number
) => {
  const value = valorAvaliacao * (convertPercentageToDecimal(percentage) ?? 0);

  return formatTwoDecimals(value);
};

const getProfitGross = (valorAvaliacao: number, valorArrematacao: number) => {
  const value = valorAvaliacao - valorArrematacao;

  return formatTwoDecimals(value);
};

export const getTaxGovIRPF = (
  valorAvaliacao: number,
  valorArrematacao: number
) => {
  const profitGross = getProfitGross(valorAvaliacao, valorArrematacao);

  if (profitGross <= 0) return 0;

  // Better read: https://myside.com.br/calculadora-ir-venda-imovel

  // Progressive tax brackets for capital gains
  if (profitGross <= 5_000_000) {
    // Up to R$ 5 million: 15%
    return formatTwoDecimals(profitGross * 0.15);
  } else if (profitGross <= 10_000_000) {
    // From R$ 5 million to R$ 10 million: 17.5%
    // First 5M at 15%, remainder at 17.5%
    return formatTwoDecimals(
      5_000_000 * 0.15 + (profitGross - 5_000_000) * 0.175
    );
  } else if (profitGross <= 30_000_000) {
    // From R$ 10 million to R$ 30 million: 20%
    // First 5M at 15%, next 5M at 17.5%, remainder at 20%
    return formatTwoDecimals(
      5_000_000 * 0.15 + 5_000_000 * 0.175 + (profitGross - 10_000_000) * 0.2
    );
  } else {
    // Over R$ 30 million: 22.5%
    // First 5M at 15%, next 5M at 17.5%, next 20M at 20%, remainder at 22.5%
    return formatTwoDecimals(
      5_000_000 * 0.15 +
        5_000_000 * 0.175 +
        20_000_000 * 0.2 +
        (profitGross - 30_000_000) * 0.225
    );
  }
};

export const getTaxITBIPercentage = (
  uf: House["uf"],
  convertToNormal?: boolean
) => {
  const value = taxes[uf].itbi.rate[0];
  return convertToNormal ? convertPercentageToNormal(value) : value;
};

export const getTaxEscriturePercentage = (
  uf: House["uf"],
  convertToNormal?: boolean
) => {
  const value = taxes[uf].escritura.rate[0];
  return convertToNormal ? convertPercentageToNormal(value) : value;
};

export const getTaxRegisterPercentage = (
  uf: House["uf"],
  convertToNormal?: boolean
) => {
  const value = taxes[uf].registro.rate[0];
  return convertToNormal ? convertPercentageToNormal(value) : value;
};

export const getTaxFRJPercentage = (
  uf: House["uf"],
  convertToNormal?: boolean
) => {
  const value = taxes[uf].frj.rate[0];
  return convertToNormal ? convertPercentageToNormal(value) : value;
};

export const calculateGeneralValues = ({
  uf,
  valor_arrematacao,
  valor_avaliacao,
  porcentagem_entrada,
  numero_de_parcelas_entrada,
  juros_anual = 12,
  assesoria_inicial = 0,
  valor_outras_despesas_inicial = 0,
  comissao_leiloeiro,
  taxa_itbi,
  taxa_escritura,
  taxa_registro,
  taxa_frj,
  valor_desocupacao = 0,
  dividas_propter_rem = 0,
  valor_outras_despesas_regularizacao = 0,
  valor_outras_despesas_venda = 0,
  valor_reforma = 0,
  comissao_corretor,
  assesoria_venda = 0,
  trafego_pago = 0,
  taxa_irpf,
  frequencia_iptu = "mensal",
  valor_iptu,
  valor_condominio,
  prazo_de_venda_meses = monthsToSell,
  banco,
  modalidade_de_venda,
  tipo,
}: CalculateGeneralValuesProps) => {
  if (!valor_arrematacao) {
    return {
      valor_de_desconto: 0,
      valor_gasto_inicial: {
        total: 0,
        valor_entrada_de_financiamento: 0,
        valor_comissao_de_leiloeiro: 0,
        valor_ITBI: 0,
        valor_registro_de_imovel: 0,
        valor_FRJ: 0,
        valor_escritura: 0,
        valor_reforma: 0,
        valor_imissao_na_posse: 0,
        valor_outras_despesas: 0,
      },
      valor_gasto_mensal: {
        total: 0,
        valor_condominio: 0,
        valor_IPTU: 0,
        valor_financiamento: 0,
      },
      valor_lucro_liquido_sozinho: 0,
      valor_lucro_liquido_com_corretor: 0,
      projecao_de_lucro: [],
      roi_solo: 0,
      roi_com_corretor: 0,
    };
  }

  const iptuValue =
    frequencia_iptu === "anual" && valor_iptu
      ? valor_iptu / yearInMonths
      : valor_iptu;
  const total_iptu =
    iptuValue ?? getTaxIPTU(valor_arrematacao, frequencia_iptu);
  const total_condominium = valor_condominio ?? getTaxCondominium(tipo);

  const formattedUf = uf.toLowerCase() as CalculateGeneralValuesProps["uf"];

  const processedLoanPercentage =
    convertPercentageToDecimal(porcentagem_entrada) ??
    bankLoanPercentage(banco, modalidade_de_venda);

  const needsFinancing =
    hasFinancingSupport(modalidade_de_venda, processedLoanPercentage) ===
    "financing";

  const taxAuctionCommision =
    comissao_leiloeiro ??
    getTaxAuctionCommision(valor_arrematacao, modalidade_de_venda);
  const taxITBI = taxa_itbi ?? getTaxITBI(valor_arrematacao, formattedUf);
  const taxFRJ = taxa_frj ?? getTaxFRJ(valor_arrematacao, formattedUf);
  const taxEscriture =
    taxa_escritura ?? getTaxEscriture(valor_arrematacao, formattedUf);
  const taxRegister =
    taxa_registro ?? getTaxRegister(valor_arrematacao, formattedUf);

  const amountEntry = needsFinancing
    ? valor_arrematacao * processedLoanPercentage
    : valor_arrematacao;

  const financedAmount = needsFinancing ? valor_arrematacao - amountEntry : 0;

  const monthlyInterestRate =
    (convertPercentageToDecimal(juros_anual) ??
      annualInterestRatePercentage(modalidade_de_venda)) / 12;

  const numberOfPayments = needsFinancing
    ? numero_de_parcelas_entrada && numero_de_parcelas_entrada > 0
      ? numero_de_parcelas_entrada
      : monthsToPay
    : 0;

  const totalAmountBankToPayPerMonth = needsFinancing
    ? (financedAmount *
        (monthlyInterestRate *
          Math.pow(1 + monthlyInterestRate, numberOfPayments))) /
      (Math.pow(1 + monthlyInterestRate, numberOfPayments) - 1)
    : 0;

  const totalToStart =
    amountEntry +
    assesoria_inicial +
    valor_outras_despesas_inicial +
    taxAuctionCommision +
    taxITBI +
    taxFRJ +
    taxEscriture +
    taxRegister +
    valor_reforma +
    valor_desocupacao +
    dividas_propter_rem +
    valor_outras_despesas_regularizacao;
  const totalToSell =
    assesoria_venda + trafego_pago + valor_outras_despesas_venda;

  const taxOnCapitalGains =
    taxa_irpf ?? getTaxGovIRPF(valor_avaliacao, valor_arrematacao);

  const profitGross = getProfitGross(valor_avaliacao, valor_arrematacao);

  const amountAgentCommision =
    comissao_corretor ?? getTaxRealStateAgent(valor_avaliacao);

  const projections = Array.from({ length: prazo_de_venda_meses }).map(
    (_, index) => {
      const month = index + 1;

      // Monthly fixed expenses (same value used in valor_gasto_mensal outside projections)
      const monthlyFixedExpenses = total_condominium + total_iptu;
      const totalMonthlyExpenses =
        monthlyFixedExpenses + totalAmountBankToPayPerMonth;

      // Total mensal expenses accumulated up to this month (2k in month 1, 4k in month 2, etc.)
      const accumulatedMonthlyExpenses = totalMonthlyExpenses * month;

      // Initial investment is made only once (at month 0)
      const initialInvestment = needsFinancing
        ? amountEntry
        : valor_arrematacao;
      const transactionCosts = totalToStart + totalToSell - amountEntry;

      // Calculate total expenses up to this specific month (initial investment + transaction costs + accumulated mensal expenses)
      const totalCashInvested =
        initialInvestment + transactionCosts + accumulatedMonthlyExpenses;

      // Total costs for profit calculation
      const totalCosts =
        valor_arrematacao + transactionCosts + accumulatedMonthlyExpenses;

      const grossProfit = valor_avaliacao - totalCosts;
      const profitAlone = grossProfit - taxOnCapitalGains;
      const profitWithAgent = profitAlone - amountAgentCommision;

      // Calculate ROI based on the total property price and all costs
      // This reflects the true return considering the full value of the property
      const totalInvestment =
        valor_arrematacao + transactionCosts + accumulatedMonthlyExpenses;

      // Calculate ROI using total investment as denominator
      const roiSolo = ((profitAlone / totalInvestment) * 100).toFixed(2);
      const roiWithAgent = ((profitWithAgent / totalInvestment) * 100).toFixed(
        2
      );

      // Calculate TIR (Internal Rate of Return) annually and monthly for this specific month
      const parsedRoiSolo = parseFloat(roiSolo) || 0;
      const tirAnual = formatTwoDecimals(parsedRoiSolo * (12 / month));
      const tirMensal = formatTwoDecimals(parsedRoiSolo / month);

      // Calculate VPL (Net Present Value) for this month
      const vpl = formatTwoDecimals(profitAlone - totalToStart);

      // Calculate SELIC investment return (14.25% annual)
      const selicAnual = 10.5;
      const selicMensal = selicAnual / 12; // Monthly SELIC rate

      // Calculate what the same amount would return if invested at SELIC rate
      // Use the same initial investment plus monthly contributions that would have gone to property expenses
      let selicInvestmentValue = totalToStart;
      let principalAmount = totalToStart; // Track principal separately from earnings

      // For each month, add the compound interest on existing amount plus monthly expenses added as investment
      for (let i = 0; i < month; i++) {
        // Calculate interest earned this month (before tax)
        const interestEarned = selicInvestmentValue * (selicMensal / 100);

        // Apply monthly SELIC rate to current value
        selicInvestmentValue += interestEarned;

        // Add the monthly expenses that would have gone to property but now go to SELIC investment
        selicInvestmentValue += totalMonthlyExpenses;
        principalAmount += totalMonthlyExpenses;
      }

      // Calculate taxable earnings (investment value minus principal)
      const totalEarnings = selicInvestmentValue - principalAmount;

      // Apply Income Tax based on investment duration
      // Brazilian regressive table for fixed income investments
      let taxRate = 0;
      if (month <= 6) {
        // Up to 180 days
        taxRate = 22.5;
      } else if (month <= 12) {
        // 181 to 360 days
        taxRate = 20;
      } else if (month <= 24) {
        // 361 to 720 days
        taxRate = 17.5;
      } else {
        // Over 720 days
        taxRate = 15;
      }

      // Calculate tax amount and net value after tax
      const taxAmount = totalEarnings * (taxRate / 100);
      const netSelicInvestmentValue = selicInvestmentValue - taxAmount;

      // Return value is what you'd get after taxes minus what you would have invested in property
      const selicReturn = formatTwoDecimals(
        netSelicInvestmentValue - totalCashInvested
      );

      return {
        mes: month,
        valor_gasto_total: totalCashInvested,
        valor_lucro_liquido_sozinho: profitAlone,
        valor_lucro_liquido_com_corretor: profitWithAgent,
        valor_gasto_mensal: totalMonthlyExpenses, // Consistent with valor_gasto_mensal.total outside
        roi_solo: parseFloat(roiSolo) || 0,
        roi_com_corretor: parseFloat(roiWithAgent) || 0,
        // Add financial metrics to each projection
        tir_anual: tirAnual,
        tir_mensal: tirMensal,
        vpl: vpl,
        retorno_selic: selicReturn,
        valor_investimento_selic: netSelicInvestmentValue,
      };
    }
  );

  return {
    valor_de_desconto: formatTwoDecimals(profitGross),
    valor_gasto_inicial: {
      total: formatTwoDecimals(totalToStart),
      valor_entrada_de_financiamento: formatTwoDecimals(amountEntry),
      valor_comissao_de_leiloeiro: formatTwoDecimals(taxAuctionCommision),
      valor_ITBI: formatTwoDecimals(taxITBI),
      valor_registro_de_imovel: formatTwoDecimals(taxRegister),
      valor_FRJ: formatTwoDecimals(taxFRJ),
      valor_escritura: formatTwoDecimals(taxEscriture),
      valor_reforma: formatTwoDecimals(valor_reforma),
      valor_imissao_na_posse: formatTwoDecimals(valor_desocupacao),
      valor_outras_despesas: formatTwoDecimals(
        valor_outras_despesas_regularizacao
      ),
      valor_dividas_propter_rem: formatTwoDecimals(dividas_propter_rem),
    },
    valor_gasto_mensal: {
      total: formatTwoDecimals(
        total_condominium + total_iptu + totalAmountBankToPayPerMonth
      ),
      valor_condominio: formatTwoDecimals(total_condominium),
      valor_IPTU: formatTwoDecimals(total_iptu),
      valor_financiamento: formatTwoDecimals(totalAmountBankToPayPerMonth),
    },
    valor_lucro_liquido_sozinho: formatTwoDecimals(
      projections[0].valor_lucro_liquido_sozinho
    ),
    valor_lucro_liquido_com_corretor: formatTwoDecimals(
      projections[0].valor_lucro_liquido_com_corretor
    ),
    projecao_de_lucro: projections.map((projection) => ({
      mes: projection.mes,
      valor_gasto_total: formatTwoDecimals(projection.valor_gasto_total),
      valor_lucro_liquido_sozinho: formatTwoDecimals(
        projection.valor_lucro_liquido_sozinho
      ),
      valor_lucro_liquido_com_corretor: formatTwoDecimals(
        projection.valor_lucro_liquido_com_corretor
      ),
      valor_gasto_mensal: formatTwoDecimals(projection.valor_gasto_mensal),
      roi_solo: formatTwoDecimals(projection.roi_solo),
      roi_com_corretor: formatTwoDecimals(projection.roi_com_corretor),
      tir_anual: formatTwoDecimals(projection.tir_anual),
      tir_mensal: formatTwoDecimals(projection.tir_mensal),
      vpl: formatTwoDecimals(projection.vpl),
      retorno_selic: formatTwoDecimals(projection.retorno_selic),
      valor_investimento_selic: formatTwoDecimals(
        projection.valor_investimento_selic
      ),
    })),
    roi_solo: formatTwoDecimals(projections[0].roi_solo),
    roi_com_corretor: formatTwoDecimals(projections[0].roi_com_corretor),
  };
};

export const hasFinancingSupport = (
  modality?: Modality,
  loanPercentage?: number
) => {
  if (loanPercentage && loanPercentage > 0 && loanPercentage < 1)
    return "financing";

  if (!modality) return "financing";

  const modalityCode = modalityReverseCriteria[modality];

  if (modalityCode === "3" || modalityCode === "4") return "financing";

  return "cash";
};

export const hasAuctionCommision = (modality?: Modality) =>
  !(modality === "Venda Direta Online" || modality === "Venda Online");
